import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { Eyebrow } from '../components/ui/Eyebrow';
import { articles, getArticle } from '../data/library';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function ArticleDetail() {
  const {
    slug = ''
  } = useParams();
  const article = getArticle(slug);
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: article ? `${article.question} — ${brand.name}` : `Not found — ${brand.name}`,
    description: article?.standfirst ?? 'This article could not be found.'
  });
  if (!article) {
    return <main className="w-full bg-cream" data-id="element-416" data-mp="tbvlou6wnnju">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-417" data-mp="tcwyp47hek47">
          <h1 className="font-serif text-4xl text-ink" data-id="element-418" data-mp="xd7a4we1m2qg">We couldn’t find that one.</h1>
          <p className="mt-5 max-w-lg leading-relaxed text-ink-soft" data-id="element-419" data-mp="yl6jwact7q7s">
            That link may be out of date — our fault, not yours. The whole library is one click
            away.
          </p>
          <Button to="/library" className="mt-8" data-id="element-420" data-mp="on0l41mav075">
            Back to the library
          </Button>
        </div>
      </main>;
  }
  const more = articles.filter(item => item.slug !== article.slug).slice(0, 3);
  return <main className="w-full bg-cream" data-id="element-421" data-mp="1hbxqj45ex2ih">
      <div className="mx-auto max-w-shell px-6 pt-10 md:px-8 md:pt-14" data-id="element-422" data-mp="1hawdiu4u65y4">
        <Link to="/library" className="inline-flex items-center gap-2 text-sm text-ink-soft transition-colors duration-150 ease-brand hover:text-brick" data-id="element-423" data-mp="1cdmh2uj14u0s">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-424" data-mp="12njq0zvgxq53" />
          The unanswered questions
        </Link>
      </div>

      <article data-id="element-425" data-mp="202xi8d4akqfg">
        <header className="bg-cream" data-id="element-426" data-mp="203gsr8rm4cjg">
          <div className="mx-auto max-w-3xl px-6 py-12 md:py-16" data-id="element-427" data-mp="1vbparmmivf4x">
            <Eyebrow className="text-brick" data-id="element-428" data-mp="1v9cleoefq2hu">{article.category}</Eyebrow>
            <h1 className="mt-5 font-serif text-[2.5rem] leading-[1.05] text-ink md:text-[3.5rem]" data-id="element-429" data-mp="3pkm24odemtfa">
              {article.question}
            </h1>
            <p className="mt-7 text-xl leading-relaxed text-ink-soft" data-id="element-430" data-mp="3fthru1yobwqs">{article.standfirst}</p>
            <p className="rule mt-8 pt-5 text-sm text-ink-muted" data-id="element-431" data-mp="3kskvpj2x3wx6">
              {article.readMinutes} min read · Reviewed by {article.reviewedBy} ·{' '}
              {article.reviewedOn}
            </p>
          </div>
        </header>

        <div className="bg-cream" data-id="element-432" data-mp="3avg6fadrzlc5">
          <div className="mx-auto max-w-3xl px-6 pb-16" data-id="element-433" data-mp="3avpz55q7g6lj">
            {article.body.map(section => <section key={section.heading} className="mt-12 first:mt-0" data-id="element-434" data-mp="2wwqahy2ozrpd">
                <h2 className="font-serif text-3xl leading-snug text-ink" data-id="element-435" data-mp="31u8kzswcyezh">{section.heading}</h2>
                {section.paragraphs.map(paragraph => <p key={paragraph} className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-436" data-mp="1023k3rwvs7cm">
                    {paragraph}
                  </p>)}
              </section>)}

            <aside className="mt-16 rounded-brand border border-brick/35 bg-cream-light p-8 md:p-10" data-id="element-437" data-mp="2fltech8kr056">
              <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-438" data-mp="2uapl8aywa747">
                What we don’t know
              </p>
              <h2 className="mt-4 font-serif text-2xl leading-snug text-ink" data-id="element-439" data-mp="2phwfbjrowsqj">
                Where the evidence runs out.
              </h2>
              <ul className="mt-6 flex flex-col" data-id="element-440" data-mp="2ybsxknhqhl0o">
                {article.unknowns.map(item => <li key={item} className="rule py-4 leading-relaxed text-ink-soft first:border-t-0 first:pt-0" data-id="element-441" data-mp="3d91x44h6xv3h">
                    {item}
                  </li>)}
              </ul>
              <p className="mt-6 text-sm text-ink-muted" data-id="element-442" data-mp="1iv0st42vgfw0">
                Every page in this library carries this section. If someone is certain about all of
                it, be careful.
              </p>
            </aside>
          </div>
        </div>
      </article>

      <section className="bg-cream-light" data-id="element-443" data-mp="513mdj9gpzfj">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-444" data-mp="9xkjh2yn3o4r">
          <Eyebrow className="text-brick" data-id="element-445" data-mp="ex6y9gu49cfo">Keep reading</Eyebrow>
          <ul className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-446" data-mp="exore4lhfajd">
            {more.map(item => <li key={item.slug} className="flex" data-id="element-447" data-mp="ixz6c6r95bad">
                <Link to={`/library/${item.slug}`} className="group flex w-full flex-col rounded-brand border border-ink/15 bg-cream p-7" data-id="element-448" data-mp="3ieou0pwm6szq">
                  <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-449" data-mp="3re24nlwh5nm1">
                    {item.category}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-ink transition-colors duration-150 ease-brand group-hover:text-brick" data-id="element-450" data-mp="17gtfl49zxv">
                    {item.question}
                  </h3>
                  <p className="rule mt-auto pt-5 text-sm text-ink-soft" data-id="element-451" data-mp="33mwpza29t1db">
                    {item.readMinutes} min read
                  </p>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-452" data-mp="2n4agherj5qg7" />
    </main>;
}